import { Component, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import {
  VdoInputTextComponent,
  VdoAutoTestIdDirective,
  VdoFieldInfoComponent,
  VdoButtonFlatComponent,
  VdoButtonStrokedComponent,
  VdoToastrService,
} from 'ui-kit';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ProfileUpdateUseCaseService } from './use-cases/profile-update-use-case.service';
import { ProfileFormToApiMapperService } from './mappers/profile-form-to-api-mapper.service';
import { ProfileApiToFormMapperService } from './mappers/profile-api-to-form-mapper.service';
import { IProfileForm } from './interfaces/profile-form.interface';
import { finalize } from 'rxjs/operators';
import { VdoToastrErrorHandler } from '@core/services/toastr-error-handler.service';
import { StatusCodeEnum } from '@core/enums/status-code.enum';
import { UserSessionStorageService } from '@core/services/user-session-storage.service';
@Component({
  selector: 'app-profile-dialog',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    VdoInputTextComponent,
    VdoAutoTestIdDirective,
    VdoFieldInfoComponent,
    TranslatePipe,
    VdoButtonFlatComponent,
    VdoButtonStrokedComponent,
  ],
  providers: [
    ProfileUpdateUseCaseService,
    ProfileFormToApiMapperService,
    ProfileApiToFormMapperService,
  ],
  templateUrl: './profile-dialog.component.html',
  styleUrl: './profile-dialog.component.scss',
})
export class ProfileDialogComponent implements OnInit {
  private readonly formBuilder = inject(FormBuilder);
  private readonly dialogRef = inject(MatDialogRef<ProfileDialogComponent>);
  private readonly profileUpdateUseCase = inject(ProfileUpdateUseCaseService);
  private readonly userSessionStorageService = inject(UserSessionStorageService);
  private readonly apiToFormMapper = inject(ProfileApiToFormMapperService);
  private readonly toastrErrorHandler = inject(VdoToastrErrorHandler);
  private readonly vdoToastrService = inject(VdoToastrService);
  private readonly translateService = inject(TranslateService);

  public profileForm!: FormGroup;
  public isLoading = signal(false);
  public emailValue = signal('');
  private originalProfileData?: IProfileForm;

  ngOnInit(): void {
    this.initializeForm();
    this.loadUserData();
  }

  private initializeForm(): void {
    this.profileForm = this.formBuilder.group({
      name: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(80)]],
    });
  }

  private loadUserData(): void {
    const profileData = this.userSessionStorageService.getUserSession();

    if (profileData) {
      const formData = this.apiToFormMapper.mapTo(profileData);
      this.originalProfileData = formData;
      this.profileForm.patchValue({ name: formData.name });

      if (formData.email) {
        this.emailValue.set(formData.email);
      }
    }
  }

  public onSave(): void {
    if (this.profileForm.invalid) {
      this.profileForm.markAllAsTouched();
      return;
    }

    const formData: IProfileForm = this.profileForm.value;

    if (this.originalProfileData?.name === formData.name) {
      this.onCancel();
      return;
    }

    this.isLoading.set(true);

    this.profileUpdateUseCase
      .execute(formData)
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe({
        next: () => {
          this.vdoToastrService.showSuccess(
            this.translateService.instant('USER_PROFILE.MESSAGES.UPDATE_SUCCESS')
          );
          this.dialogRef.close({ success: true, updatedData: formData });
        },
        error: error => {
          console.error('Error updating profile:', error);
          this.toastrErrorHandler.showError(error, [
            StatusCodeEnum.BAD_REQUEST,
            StatusCodeEnum.FORBIDDEN,
            StatusCodeEnum.NOT_FOUND,
          ]);
        },
      });
  }

  public onCancel(): void {
    this.dialogRef.close();
  }

  public get nameControl() {
    return this.profileForm.get('name');
  }
}
